import { apiClient } from 'lib/api';
import { useMutation } from '@tanstack/react-query';
import type { MutationConfig } from 'lib/react-query';
import { isAxiosError } from 'axios';
import type { AdSetEntry } from './getAdSets';
import type { CreativePackEntry } from '../types';

export interface CreateFacebookAdFromPacksParams {
  app?: string;
  platform?: string;
  creative_packs: CreativePackEntry[];
  ad_sets: AdSetEntry[] | null;
  call_to_action?: string;
  hotjar?: boolean;
  conversion_domain?: string | null;
  optional_ad_label?: string;
  visual_touch_ups?: boolean;
  hypothesis?: string;
  scaling_flows?: null;
  source?: string;
}

export interface CreateFacebookAdResponse {
  job_id: string;
}

const createFacebookAd = async (body: CreateFacebookAdFromPacksParams): Promise<CreateFacebookAdResponse> => {
  const res = await apiClient.post<CreateFacebookAdResponse>(
    '/api/v1/facebook_manager/create_ad_from_packs',
    body,
  );

  if (isAxiosError(res)) {
    const error = new Error('Create FacebookAd Error');
    error.cause = {
      ...res.response?.data,
      status: res.response?.status,
    };
    return Promise.reject(error);
  }
  return res.data;
};

const createFacebookAdLocal = (body: CreateFacebookAdFromPacksParams): Promise<CreateFacebookAdResponse> => {
  console.log('[DEV] create_ad_from_packs payload:', body);
  return new Promise((resolve) => {
    resolve({ job_id: 'mock-job-id' });
  });
};

const submitFacebookAd =
  process.env.NODE_ENV === 'development'
    ? createFacebookAdLocal
    : createFacebookAd;

interface UseCreateFacebookAdOptions {
  config?: MutationConfig<typeof submitFacebookAd>;
}

const useCreateFacebookAd = ({ config }: UseCreateFacebookAdOptions = {}) => {
  return useMutation({
    ...config,
    onSuccess: (...args) => {
      if (config?.onSuccess) {
        config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: submitFacebookAd,
  });
};

export { createFacebookAd, useCreateFacebookAd };